/**
 * useWhiteboard: ExcalidrawApp 생명주기 관리 훅
 * v1 원본: utils/whiteboard/useWhiteboard.js (1300줄)
 * v2 변환: TypeScript, hooks 전용 (~250줄)
 *
 * 역할:
 * - ExcalidrawApp ref 관리
 * - 메타데이터에서 roomId/roomKey/totalPages 추출
 * - 배경색/격자 Redux ↔ Excalidraw 동기화
 * - 페이지 네비게이션 (문서공유)
 * - collabAPI portal 정리
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { whiteboardActions, type CollabDetails } from '@/store/slices/whiteboardSlice';
import { toastActions } from '@/store/slices/toastSlice';
import { METADATA_KEYS, DOC_SHARE_BACKGROUND, COLLAB_SERVER_URL } from '@/utils/whiteboard/constants';
import { getRoomInfo } from '@/utils/whiteboard';
import {
  useFullscreen,
  useWhiteboardTools,
  useWhiteboardZoom,
  loadDeferredFiles,
  type ExcalidrawAPI,
} from '@/hooks/useWhiteboardCommon';

interface UseWhiteboardOptions {
  isViewOnly?: boolean;
  isSubVideoMode?: boolean;
  initialZoom?: number;
}

export function useWhiteboard(options: UseWhiteboardOptions = {}) {
  const {
    isViewOnly: initialIsViewOnly = false,
    isSubVideoMode = false,
    initialZoom = 1,
  } = options;

  const dispatch = useAppDispatch();
  const whiteboard = useAppSelector((s) => s.whiteboard);
  const local = useAppSelector((s) => s.local);

  const excalidrawApi = useRef<ExcalidrawAPI | null>(null);
  const isDocShareModeRef = useRef(false);
  const backgroundPropsRef = useRef(whiteboard.backgroundProps);
  const participantsRef = useRef<Array<{ isOwner?: boolean; jitsiId?: string; roomIndex?: number }>>([]);

  const [isValid, setIsValid] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(initialIsViewOnly);
  const [isSessionOwner, setIsSessionOwner] = useState(false);
  const [pdfFileName, setPdfFileName] = useState('화이트보드');
  const [drawFileList, setDrawFileList] = useState<unknown[]>([]);
  const [editPermissions, setEditPermissions] = useState<unknown>(null);
  const [participants, setParticipants] = useState<Array<{ isOwner?: boolean; jitsiId?: string; roomIndex?: number; canEdit?: boolean }>>([]);
  const [gapTop, setGapTop] = useState(0);
  const prevIsViewOnlyRef = useRef<boolean | null>(null);
  const whiteboardRef = useRef<HTMLDivElement>(null);

  // 공통 훅
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const { currentTool, setCurrentTool, isOpenDiagramPopover, setIsOpenDiagramPopover, onClickTools } =
    useWhiteboardTools(excalidrawApi);
  const { zoomNumber, setZoomNumber, onClickZoomIn, onClickZoomOut } =
    useWhiteboardZoom(excalidrawApi, initialZoom);

  // ─── 유효성 + 모드 판별 ───────────────────────────────────────────────────
  const isDocShareMode = whiteboard.isOpen && !whiteboard.isSketchMode;
  isDocShareModeRef.current = isDocShareMode;
  backgroundPropsRef.current = whiteboard.backgroundProps;

  // 문서공유: 현재 페이지의 collabDetails (collabDetailList에서 추출)
  const currentPageCollabDetails = (() => {
    if (!isDocShareMode || whiteboard.collabDetailList.length === 0) {
      return whiteboard.collabDetails;
    }
    const found = whiteboard.collabDetailList.find(
      (item) => item.pageNum === whiteboard.currentPage + 1  // currentPage는 0-indexed, pageNum은 1-indexed
    );
    return found?.collabDetails ?? whiteboard.collabDetails;
  })();

  const roomId = currentPageCollabDetails?.roomId ?? null;
  const roomKey = currentPageCollabDetails?.roomKey ?? null;
  const totalPages = whiteboard.totalPages;
  const currentPage = whiteboard.currentPage;

  useEffect(() => {
    setIsValid(whiteboard.isOpen && !!roomId && !!roomKey);
    setIsSessionOwner(whiteboard.isOwner);
    if (whiteboard.fileMetadata?.fileName) {
      setPdfFileName(whiteboard.fileMetadata.fileName);
    }
  }, [whiteboard.isOpen, roomId, roomKey, whiteboard.isOwner, whiteboard.fileMetadata]);

  // v1 패턴: collab 서버에서 pdfFileName 조회 (getRoomInfo)
  // 원격 참여자가 문서공유를 받는 경우 fileMetadata.fileName이 비어있을 수 있음
  useEffect(() => {
    if (!whiteboard.isOpen) return;
    const room = (window as any)._jitsiRoom;
    const conferenceId = room?.getName?.() ?? room?.options?.name;
    if (!conferenceId) return;

    getRoomInfo(conferenceId).then((result) => {
      if (result?.pdfFileName) {
        setPdfFileName(result.pdfFileName);
      }
      if (result?.editPermissions !== undefined) {
        setEditPermissions(result.editPermissions);
      }
    });
  }, [whiteboard.isOpen]);

  // v1 패턴: metadata에서 직접 파일 목록 읽기 (Redux 대비 fallback)
  // v1은 metadataHandler.getMetadata() 동기 읽기를 매 렌더마다 수행
  const getFilesFromMetadata = useCallback((): Array<{ id: string; thumbnailURL: string; mimeType: string; dataURL: string | null; created: number; [k: string]: unknown }> => {
    try {
      const room = (window as any)._jitsiRoom;
      const handler = room?.getMetadataHandler?.();
      if (!handler) return [];
      const md = handler.getMetadata();
      const fm = md?.[METADATA_KEYS.FILE_METADATA] as { files?: Array<any> } | undefined;
      if (!fm?.files || !Array.isArray(fm.files)) return [];
      return fm.files.map((f: any) => ({
        ...f,
        thumbnailURL: f.thumbnailURL?.replace(
          'http://dev.api0.wehago.com/',
          'https://rtctest.wehago.com/api0/',
        ) ?? f.thumbnailURL,
      }));
    } catch {
      return [];
    }
  }, []);

  // v1 패턴: files를 ref에 저장하여 getExcalidrawAPI 콜백에서 항상 최신 값 참조
  const filesRef = useRef(whiteboard.docShareFiles);
  filesRef.current = whiteboard.docShareFiles;

  // fallback: Redux docShareFiles가 비어있으면 metadata에서 직접 읽어 동기화
  useEffect(() => {
    if (isDocShareMode && whiteboard.docShareFiles.length === 0 && totalPages > 0) {
      const metaFiles = getFilesFromMetadata();
      if (metaFiles.length > 0) {
        dispatch(whiteboardActions.setDocShareFiles(
          metaFiles.map(f => ({
            mimeType: f.mimeType || 'image/png',
            id: f.id,
            dataURL: f.dataURL ?? null,
            created: f.created || Date.now(),
            thumbnailURL: f.thumbnailURL,
          }))
        ));
      }
    }
  }, [isDocShareMode, whiteboard.docShareFiles.length, totalPages, dispatch, getFilesFromMetadata]);

  // ─── ExcalidrawApp 초기화 시 배경 동기화 ──────────────────────────────────
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const initInterval = setInterval(() => {
      if (excalidrawApi.current) {
        clearInterval(initInterval);

        if (isDocShareModeRef.current) {
          excalidrawApi.current.updateScene({
            appState: { ...DOC_SHARE_BACKGROUND },
          });
        } else {
          excalidrawApi.current.updateScene({
            appState: backgroundPropsRef.current,
          });
        }

        // onChange: v1 패턴 — ref 비교로 실제 변경 시에만 dispatch
        if (typeof excalidrawApi.current.onChange === 'function') {
          unsubscribe = excalidrawApi.current.onChange((_elements, appState) => {
            if (!appState) return;

            // 문서공유 모드: 배경 변경 감지 시 강제로 복원, dispatch 안 함
            if (isDocShareModeRef.current) {
              if (appState.viewBackgroundColor !== DOC_SHARE_BACKGROUND.viewBackgroundColor ||
                  !appState.gridModeEnabled ||
                  appState.gridSize !== DOC_SHARE_BACKGROUND.gridSize) {
                excalidrawApi.current?.updateScene({ appState: DOC_SHARE_BACKGROUND });
              }
              return;
            }

            // 스케치 모드: 값이 실제로 변했을 때만 dispatch
            const currentProps = backgroundPropsRef.current;
            const viewBackgroundColor = (appState.viewBackgroundColor as string) || '#ffffff';
            const gridModeEnabled = (appState.gridModeEnabled as boolean) || false;
            const gridSize = (appState.gridSize as number) || 20;

            const gridType = gridModeEnabled ? (gridSize === 40 ? 'type2' : 'type1') : 'none';
            const currentGridType = currentProps.gridModeEnabled ? (currentProps.gridSize === 40 ? 'type2' : 'type1') : 'none';
            const currentBgColor = currentProps.viewBackgroundColor || '#ffffff';

            if (currentBgColor !== viewBackgroundColor || currentGridType !== gridType) {
              backgroundPropsRef.current = {
                viewBackgroundColor,
                gridModeEnabled,
                gridSize: Math.max(gridSize, 20),
              };
              dispatch(whiteboardActions.setBackgroundProps({
                viewBackgroundColor,
                gridModeEnabled,
                gridSize: Math.max(gridSize, 20),
              }));
            }
          }) as unknown as (() => void) | undefined;
        }

        setIsLoadingPage(true);
      }
    }, 500);

    return () => {
      clearInterval(initInterval);
      unsubscribe?.();
    };
  }, [currentPage, dispatch]);

  // ─── collabAPI portal cleanup (페이지 전환) ───────────────────────────────
  useEffect(() => {
    const portal = (window as any).collabAPI?.portal;
    return () => {
      portal?.close?.();
    };
  }, [currentPage]);

  // ─── 페이지 네비게이션 ─────────────────────────────────────────────────────
  const handlePrevPage = useCallback(() => {
    if (currentPage > 0) {
      setIsLoadingPage(false);
      dispatch(whiteboardActions.setCurrentPage(currentPage - 1));
    }
  }, [currentPage, dispatch]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setIsLoadingPage(false);
      dispatch(whiteboardActions.setCurrentPage(currentPage + 1));
    }
  }, [currentPage, totalPages, dispatch]);

  const handlePageChange = useCallback((page: number) => {
    if (page >= 0 && page < totalPages && page !== currentPage) {
      setIsLoadingPage(false);
      dispatch(whiteboardActions.setCurrentPage(page));
    }
  }, [totalPages, currentPage, dispatch]);

  // ─── 편집 권한(뷰어/공동편집) 동적 변경 ─────────────────────────────────────
  const handleToggleEditorMode = useCallback(async (isAllEditable: boolean) => {
    if (!isSessionOwner) return;
    const room = (window as any)._jitsiRoom;
    if (!room) return;

    const conferenceId = room.getName?.() ?? room.options?.name ?? '';
    const ownerId = room.myUserId?.();

    try {
      const { data } = await axios({
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        url: `${COLLAB_SERVER_URL}/api/participants-permissions`,
        data: {
          conferenceId,
          editPermissions: isAllEditable ? null : [ownerId],
        },
      });
      // 서버 전송이 성공하면 즉각적으로 본인 컴포넌트 업데이트
      setEditPermissions(isAllEditable ? null : [ownerId]);
    } catch (e) {
      console.error('[Whiteboard] 권한 변경 실패:', e);
      alert('권한 변경에 실패했습니다. 다시 시도해 주세요.');
    }
  }, [isSessionOwner]);

  // ─── onChangeParticipants: v1 useWhiteboard.js 원본 패턴 ────────────────
  // Excalidraw collab에서 참여자 목록이 변경될 때 호출
  // 오너의 roomIndex가 바뀌면 다른 참여자가 해당 페이지로 자동 이동
  const onChangeParticipants = useCallback((p: Array<{ isOwner?: boolean; jitsiId?: string; roomIndex?: number; canEdit?: boolean }>) => {
    if (p && p.length > 0) {
      const ownerAfter = p.find((pp) => pp.isOwner);
      const ownerBefore = participantsRef.current.find((pp) => pp.isOwner);
      // 오너가 페이지를 이동한 경우에만 따라감
      // ownerBefore가 없으면(첫 접속) 페이지 변경하지 않음
      if (
        ownerAfter &&
        ownerBefore &&
        ownerAfter.roomIndex !== ownerBefore.roomIndex &&
        currentPage !== (ownerAfter.roomIndex ?? 0)
      ) {
        dispatch(whiteboardActions.setCurrentPage(ownerAfter.roomIndex ?? 0));
      }
      // 현재 사용자가 오너인지 확인 + 권한 확인
      const room = (window as any)._jitsiRoom;
      if (room) {
        const isLocallyOwner = ownerAfter ? ownerAfter.jitsiId === room.myUserId?.() : false;
        if (ownerAfter) {
          setIsSessionOwner(isLocallyOwner);
        }
        if (!initialIsViewOnly) {
          const isParticipantAbled = p.some(
            (pp) => pp.jitsiId === room.myUserId?.() && pp.canEdit
          );
          // 공유자(방장)이거나 편집 권한이 있으면 뷰어 모드가 아님 (스케치 가능)
          const nextIsViewOnly = !(isLocallyOwner || isParticipantAbled);
          // 오너가 아닌 참석자에게 공동편집 모드 전환 스낵바 표시
          if (!isLocallyOwner && prevIsViewOnlyRef.current !== null && prevIsViewOnlyRef.current !== nextIsViewOnly) {
            dispatch(toastActions.showSnackbar({
              type: 'info',
              message: nextIsViewOnly
                ? '공동 편집이 종료되었어요.'
                : '공동 편집이 활성화 되었어요. 함께 스케치해보세요!',
            }));
          }
          prevIsViewOnlyRef.current = nextIsViewOnly;
          setIsViewOnly(nextIsViewOnly);
        }
      }
    }
    participantsRef.current = p;
    setParticipants([...p]);
  }, [currentPage, dispatch, initialIsViewOnly]);

  // ─── detectDefferredLoad: v1 useWhiteboard.js 원본 패턴 ────────────────
  const detectDefferredLoad = useCallback(async (
    fileList: Array<{ id: string; thumbnailURL: string; [k: string]: unknown }>,
    excalidrawApis: ExcalidrawAPI | null = excalidrawApi.current,
  ) => {
    if (fileList && fileList.length > 0) setIsFilesLoading(true);
    await loadDeferredFiles(fileList, excalidrawApis, setDrawFileList);
    // 문서공유 모드에서는 scrollToContent 완료 후 해제 (아래 scrollTimer에서 처리)
    if (!isDocShareMode && !isSubVideoMode) {
      setIsFilesLoading(false);
    }
  }, [isDocShareMode, isSubVideoMode]);

  // ─── Excalidraw API 콜백: v1 WhiteboardPresenter.js getExcalidrawAPI 원본 패턴 ──
  const handleGetExcalidrawAPI = useCallback((api: ExcalidrawAPI) => {
    if (excalidrawApi.current === api) return;
    (window as any).excalidrawAPI = api; // 디버깅용
    excalidrawApi.current = api;

    // v1: detectDefferredLoad(files, api) — filesRef.current = 전체 파일 목록
    // fallback: Redux가 아직 비어있으면 metadata에서 직접 읽기 (v1 원본 패턴)
    let fileList = filesRef.current as any[];
    if (!fileList || fileList.length === 0) {
      fileList = getFilesFromMetadata();
    }
    detectDefferredLoad(fileList, api);

    // gapTop 계산
    const container = document.querySelector('.excalidraw-container');
    if (container) {
      setGapTop(container.getBoundingClientRect().top);
    }

    // v1: 문서공유 모드일 때만 씬 데이터 로드 후 가운데 정렬
    if (isDocShareMode) {
      const scrollTimer = setInterval(() => {
        const elements = api.getSceneElements();
        if (elements?.length > 0) {
          clearInterval(scrollTimer);
          api.scrollToContent(elements, {
            fitToViewport: !!isSubVideoMode,
            fitToContent: true,
          });
          setIsFilesLoading(false);
        }
      }, 1000);
      setTimeout(() => { clearInterval(scrollTimer); setIsFilesLoading(false); }, 10000);
    } else if (isSubVideoMode) {
      // v1: 서브비디오 모드(일반 화이트보드): 모든 컨텐츠가 뷰포트에 맞도록
      const scrollTimer = setInterval(() => {
        const elements = api.getSceneElements();
        if (elements?.length > 0) {
          clearInterval(scrollTimer);
          api.scrollToContent(elements, { fitToViewport: true, fitToContent: true });
          setIsFilesLoading(false);
        }
      }, 1000);
      setTimeout(() => { clearInterval(scrollTimer); setIsFilesLoading(false); }, 10000);
    } else {
      api.scrollToContent();
    }
  }, [detectDefferredLoad, isDocShareMode, isSubVideoMode]);

  const handleGetCollabAPI = useCallback((collabAPI: { setUsername: (name: string) => void }) => {
    // 모바일 페이지(getCollabAPI)와 동일하게 전역 노출 — 위쪽 230번째 줄의 collabAPI portal cleanup effect가
    // 이 값을 참조하는데, 지금까지 아무도 세팅해주지 않아 항상 undefined였다.
    (window as any).collabAPI = collabAPI;
    const displayName = local.userName || local.nickname || local.userId || local.wehagoId || '';
    collabAPI.setUsername(displayName);
  }, [local.userName, local.nickname, local.userId, local.wehagoId]);

  // ─── 높이 계산 ────────────────────────────────────────────────────────────
  const wrapperHeight = isFullscreen ? '100vh' : `calc(100% - ${gapTop}px)`;

  return {
    // 상태
    isValid,
    isLoadingPage,
    isFilesLoading,
    setIsFilesLoading,
    isViewOnly,
    isSessionOwner,
    isDocShareMode,
    isFullscreen,
    pdfFileName,
    drawFileList,
    setDrawFileList,
    docShareFiles: whiteboard.docShareFiles,
    wrapperHeight,
    gapTop,
    setGapTop,

    // refs
    excalidrawApi,
    whiteboardRef,

    // 페이지 데이터
    roomId,
    roomKey,
    totalPages,
    currentPage,
    editPermissions,
    participants,

    // 도구
    currentTool,
    setCurrentTool,
    isOpenDiagramPopover,
    setIsOpenDiagramPopover,
    onClickTools,

    // 줌
    zoomNumber,
    setZoomNumber,
    onClickZoomIn,
    onClickZoomOut,

    // 액션
    handlePrevPage,
    handleNextPage,
    handlePageChange,
    handleToggleEditorMode,
    handleGetExcalidrawAPI,
    handleGetCollabAPI,
    onChangeParticipants,
    toggleFullscreen,
  };
}
